/*
 * Branded entity ID types (ADR 0007).
 *
 * Every entity id is a distinct nominal type, not a bare `string`, so passing a
 * ClientId where an InvoiceId is expected fails at compile time. The brand is a
 * phantom tag that vanishes at runtime — zero runtime cost, zero runtime change.
 *
 * Branding happens ONLY at the two creation boundaries:
 *   1. Route param parse (the router hands a `string`; the route brands it once
 *      before it flows into a query/mutation).
 *   2. Test fixture factories (`test/factories/*` mint branded ids).
 *
 * Downstream code — domain fns, query keys, query hooks, mutations, component
 * props — carries the branded type through UNCHANGED. A mid-flow `as SomeId`
 * cast is banned: if tsc complains, the type flow is wrong, not the brand.
 */

declare const brand: unique symbol;

/** A nominal `string` tagged with `B`. Assignable to `string`; not interchangeable with other brands. */
export type Brand<T, B> = T & { readonly [brand]: B };

export type EntityId = Brand<string, 'EntityId'>;
export type RelationshipId = Brand<string, 'RelationshipId'>;
export type ClientId = Brand<string, 'ClientId'>;
export type TpaId = Brand<string, 'TpaId'>;
export type ProviderId = Brand<string, 'ProviderId'>;
export type AgentBrokerId = Brand<string, 'AgentBrokerId'>;
export type GeneralAgentId = Brand<string, 'GeneralAgentId'>;
export type EntityContractId = Brand<string, 'EntityContractId'>;
export type InvoiceId = Brand<string, 'InvoiceId'>;
export type JobId = Brand<string, 'JobId'>;
export type UserId = Brand<string, 'UserId'>;

/*
 * Brand constructors — the ONLY sanctioned way to create a branded id. Use at
 * the creation boundaries (route param parse, fixture factories). These are the
 * narrowing counterpart to `unwrap` (spec §6): a deliberate boundary function,
 * not a mid-flow assertion scattered through the call graph.
 */
export const entityId = (id: string): EntityId => id as EntityId;
export const relationshipId = (id: string): RelationshipId =>
  id as RelationshipId;
export const clientId = (id: string): ClientId => id as ClientId;
export const tpaId = (id: string): TpaId => id as TpaId;
export const providerId = (id: string): ProviderId => id as ProviderId;
export const agentBrokerId = (id: string): AgentBrokerId => id as AgentBrokerId;
export const generalAgentId = (id: string): GeneralAgentId =>
  id as GeneralAgentId;
export const entityContractId = (id: string): EntityContractId =>
  id as EntityContractId;
export const invoiceId = (id: string): InvoiceId => id as InvoiceId;
export const jobId = (id: string): JobId => id as JobId;
export const userId = (id: string): UserId => id as UserId;
